import React from "react";
import { useNavigate } from "react-router-dom";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";

import SoftBox from "../../components/SoftBox";
import SoftTypography from "../../components/SoftTypography";

import { UseIsAdmin } from "../../hooks/auth";
import { T, FONT, CARD_SX, CARD_TITLE_SX, TONE, scoreBand, FIGURE_SX } from "./bidQualityTokens";

/**
 * Presentational pieces shared by the Bid Quality form and the submissions
 * review, so a "Yes" answer, a score chip and a card frame look identical on
 * both pages instead of being re-invented per file.
 */

/**
 * Rate a job / Submissions toggle, rendered inside the PageHeader banner —
 * same control Claims and Team Feed use for their header toggles. Submissions
 * is an admin-only route, so players see no toggle at all rather than a tab
 * that would bounce them to the unauthorized page.
 */
export const BidQualityTabs = ({ active = "form" }) => {
  const isAdmin = UseIsAdmin();
  const navigate = useNavigate();
  if (!isAdmin) return null;

  const tabs = [
    { key: "form", label: "Rate a job", route: "/bid-quality" },
    { key: "reviews", label: "Submissions", route: "/bid-quality/reviews" },
    { key: "settings", label: "Settings", route: "/bid-quality/settings" },
  ];
  const value = Math.max(tabs.findIndex((t) => t.key === active), 0);

  return (
    <Tabs
      orientation="horizontal"
      value={value}
      onChange={(_, next) => navigate(tabs[next].route)}
      sx={{
        background: "#fff!important",
        borderRadius: "8px",
        padding: "4px",
        border: "1px solid #EAECF0",
        width: "max-content",
        minHeight: "unset",
        "& .MuiTabs-indicator": { borderRadius: "6px", boxShadow: "none" },
      }}
    >
      {tabs.map((t) => (
        <Tab
          key={t.key}
          label={t.label}
          sx={{ color: "#343A40", fontSize: "0.8rem", minHeight: "32px", py: "6px", px: "16px" }}
        />
      ))}
    </Tabs>
  );
};

/**
 * The house card frame with an optional titled head. `action` renders at the
 * right of the head (export button, counters, ...).
 */
export const SectionCard = ({ title, caption, action, children, bodySx = {}, sx = {} }) => (
  <SoftBox sx={{ ...CARD_SX, overflow: "hidden", ...sx }}>
    {(title || action) && (
      <SoftBox
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          padding: "14px 20px",
          borderBottom: `1px solid ${T.hairline}`,
          backgroundColor: T.subtle,
        }}
      >
        <SoftBox sx={{ minWidth: 0 }}>
          {title && <SoftTypography sx={CARD_TITLE_SX}>{title}</SoftTypography>}
          {caption && (
            <SoftTypography sx={{ fontFamily: FONT, fontSize: "12px", fontWeight: 400, color: T.muted, mt: "2px" }}>
              {caption}
            </SoftTypography>
          )}
        </SoftBox>
        {action}
      </SoftBox>
    )}
    <SoftBox sx={{ padding: "20px", ...bodySx }}>{children}</SoftBox>
  </SoftBox>
);

/**
 * A stored answer. Colour alone never carries the meaning — the glyph and the
 * word do too, so it still reads on a mono printout or to a colourblind user.
 * @param {boolean|null} value true = Yes, false = No, null = never answered
 */
export const AnswerPill = ({ value, iconOnly = false }) => {
  let tone = TONE.neutral;
  let Icon = RemoveRoundedIcon;
  let word = "—";
  if (value === true) {
    tone = TONE.good;
    Icon = CheckRoundedIcon;
    word = "Yes";
  } else if (value === false) {
    tone = TONE.bad;
    Icon = CloseRoundedIcon;
    word = "No";
  }

  return (
    <SoftBox
      component="span"
      title={iconOnly ? word : undefined}
      aria-label={iconOnly ? word : undefined}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: iconOnly ? 0 : "4px",
        minWidth: iconOnly ? "26px" : "auto",
        height: "26px",
        padding: iconOnly ? 0 : "0 10px",
        borderRadius: iconOnly ? "50%" : "999px",
        backgroundColor: tone.fill,
        border: `1px solid ${tone.border}`,
        color: tone.ink,
        fontFamily: FONT,
        fontSize: "12px",
        fontWeight: 600,
        lineHeight: 1,
      }}
    >
      <Icon sx={{ fontSize: "1rem" }} />
      {!iconOnly && word}
    </SoftBox>
  );
};

/** 0-100 job score as a banded chip. */
export const ScoreChip = ({ score, size = "md" }) => {
  const band = scoreBand(score);
  const known = Number.isFinite(Number(score));
  return (
    <SoftBox
      component="span"
      title={band.label}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        height: size === "sm" ? "24px" : "28px",
        padding: size === "sm" ? "0 8px" : "0 10px",
        borderRadius: "8px",
        backgroundColor: band.fill,
        border: `1px solid ${band.border}`,
        color: band.ink,
        ...FIGURE_SX,
        fontSize: size === "sm" ? "12.5px" : "13.5px",
        fontWeight: 700,
      }}
    >
      {known ? `${Number(score)}%` : "—"}
    </SoftBox>
  );
};

/** Inline notice — used for "no Sales workspace", load errors and warnings. */
export const Notice = ({ tone = "warn", title, children, action }) => {
  const t = TONE[tone] || TONE.warn;
  return (
    <SoftBox
      role={tone === "bad" ? "alert" : undefined}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: "10px",
        padding: "12px 14px",
        borderRadius: "10px",
        backgroundColor: t.fill,
        border: `1px solid ${t.border}`,
      }}
    >
      <SoftBox sx={{ flex: 1, minWidth: 0 }}>
        {title && (
          <SoftTypography sx={{ fontFamily: FONT, fontSize: "13px", fontWeight: 600, color: t.ink }}>
            {title}
          </SoftTypography>
        )}
        <SoftTypography sx={{ fontFamily: FONT, fontSize: "12.5px", fontWeight: 400, color: T.ink2, lineHeight: 1.5 }}>
          {children}
        </SoftTypography>
      </SoftBox>
      {action}
    </SoftBox>
  );
};
